// Firestore access for IIL employee advances and their payroll recoveries.
import { addDoc, collection, doc, getDocs, increment, query, serverTimestamp, updateDoc, where, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { advancePositions, normalizeCode, round, validateAdvance } from "./iilAdvanceCalculator";

const ADVANCES = "iil_advances";
const RECOVERIES = "iil_advance_recoveries";
const MASTER = "iil_employee_mappings";

const text = (value) => String(value ?? "").trim();
const actor = () => ({ uid: auth.currentUser?.uid || null, email: auth.currentUser?.email || null });

export async function loadAdvanceLedger() {
  const read = async (name) => (await getDocs(collection(db, name))).docs.map((item) => ({ id: item.id, ...item.data() }));
  const [advances, recoveries] = await Promise.all([read(ADVANCES), read(RECOVERIES)]);
  return { advances, recoveries };
}

export async function loadEmployeeMaster() {
  const rows = (await getDocs(collection(db, MASTER))).docs.map((item) => ({ id: item.id, ...item.data() }));
  return rows.sort((a, b) => text(a.employeeCode).localeCompare(text(b.employeeCode), undefined, { numeric: true }));
}

// Bank details are copied onto the advance so the payment file stays correct
// even if the employee's master record changes later.
export async function createAdvance(input) {
  const problem = validateAdvance(input);
  if (problem) throw new Error(problem);
  const who = actor();
  const fields = {
    employeeCode: normalizeCode(input.employeeCode), employeeName: text(input.employeeName), clientEmpId: text(input.clientEmpId),
    segment: text(input.segment), fatherName: text(input.fatherName), department: text(input.department), designation: text(input.designation), cnic: text(input.cnic), bank: text(input.bank), account: text(input.account), pmtMode: text(input.pmtMode),
    amount: round(input.amount), givenDate: input.givenDate, emailRef: text(input.emailRef), note: text(input.note),
    installment: input.installment === "" || input.installment == null ? null : round(input.installment),
    paymentStatus: "PENDING", exportCount: 0,
    entryStatus: "ACTIVE", seq: Date.now(),
    createdBy: who.uid, createdByEmail: who.email, createdAt: serverTimestamp()
  };
  const ref = await addDoc(collection(db, ADVANCES), fields);
  return { id: ref.id, ...fields };
}

export async function rejectAdvance(row, reason, recoveries) {
  if (!text(reason)) throw new Error("A reason is required to reject an advance.");
  const position = advancePositions({ advances: [row], recoveries })[0];
  if (position && position.recovered > 0) throw new Error("Part of this advance has already been recovered in a payroll, so it cannot be rejected.");
  const who = actor();
  await updateDoc(doc(db, ADVANCES, row.id), {
    entryStatus: "REJECTED", rejectedReason: text(reason), rejectedByEmail: who.email, rejectedAt: serverTimestamp()
  });
}

export async function markExported(ids) {
  const who = actor();
  const batch = writeBatch(db);
  ids.forEach((id) => batch.update(doc(db, ADVANCES, id), {
    exportedAt: serverTimestamp(), exportedByEmail: who.email, exportCount: increment(1)
  }));
  await batch.commit();
}

export async function markPaid(ids, { paidDate, bankReference }) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(paidDate || ""))) throw new Error("Enter the date the advance was paid.");
  const who = actor();
  const batch = writeBatch(db);
  ids.forEach((id) => batch.update(doc(db, ADVANCES, id), {
    paymentStatus: "PAID", paidDate, bankReference: text(bankReference), paidMarkedByEmail: who.email, paidMarkedAt: serverTimestamp()
  }));
  await batch.commit();
}

// Called when a payroll month is completed. Ids are deterministic, so
// completing the month again after a reopen simply replaces the earlier ones.
export async function postRecoveries(monthKey, plan) {
  const who = actor();
  const batch = writeBatch(db);
  let count = 0;
  plan.byCode.forEach((row) => row.allocations.forEach((allocation) => {
    if (!(allocation.amount > 0)) return;
    batch.set(doc(db, RECOVERIES, `${monthKey}_${allocation.advanceId}`), {
      advanceId: allocation.advanceId, employeeCode: row.code, employeeName: row.name, monthKey,
      amount: round(allocation.amount), status: "ACTIVE", postedByEmail: who.email, postedAt: serverTimestamp()
    });
    count += 1;
  }));
  if (count) await batch.commit();
  return count;
}

// Called when a completed payroll is reopened, so the money is owed again
// until the revised payroll is completed.
export async function reverseRecoveries(monthKey) {
  const result = await getDocs(query(collection(db, RECOVERIES), where("monthKey", "==", monthKey)));
  const active = result.docs.filter((item) => item.data().status !== "REVERSED");
  if (!active.length) return 0;
  const who = actor();
  const batch = writeBatch(db);
  active.forEach((item) => batch.update(item.ref, { status: "REVERSED", reversedByEmail: who.email, reversedAt: serverTimestamp() }));
  await batch.commit();
  return active.length;
}
